import { AdvancedMarker, APIProvider, InfoWindow, Map } from "@vis.gl/react-google-maps";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { FaBath, FaBed, FaBuilding, FaHome, FaMapMarker, FaWarehouse } from "react-icons/fa";
import { LiaVectorSquareSolid } from "react-icons/lia";
import PropTypes from 'prop-types';
import './MultiMap.css'; // Import the CSS file
import { Link } from "react-router-dom";
import { Image } from "react-bootstrap";
import samplePic from '../../assets/no-image.webp';

/**
 * MultiMap Component - Displays multiple property locations on a Google Map
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.filteredProperty - Array of property objects to display on the map
 * @returns {JSX.Element} - Rendered component
 */
export const MultiMap = ({ filteredProperty }) => {
    // Get the API key from environment variables rather than importing directly
    const MAPS_API_KEY = process.env.REACT_APP_GMAP_KEY;
    const [locations, setLocations] = useState([]);
    const infoWindowRef = useRef(null);
    const [activeMarker, setActiveMarker] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    /**
     * Processes property data into a format suitable for map rendering
     * 
     * @param {Array} property - Array of property objects
     */
    const setPropertyData = useCallback((property) => {
        try {
            if (!Array.isArray(property)) {
                throw new Error('Property data is not an array');
            }

            const Locations = property.map((p, index) => {
                // Validate required properties exist
                if (!p?.Unit?.lat || !p?.Unit?.long) {
                    console.warn(`Property at index ${index} missing coordinates`);
                }

                return {
                    id: index + 1,
                    lat: p?.Unit?.lat,
                    lng: p?.Unit?.long,
                    propertyType: p?.Unit?.propertSubyType || 'Residential',
                    title: p?.Property?.PropertyUnitTypeTitle || 'Property',
                    file: p?.Property?.Files,
                };
            });
            
            setLocations(Locations);
            setIsLoading(false);
        } catch (error) {
            console.error('Error processing property data:', error);
            setHasError(true);
            setErrorMessage('Failed to process property data');
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        setIsLoading(true);
        setHasError(false);
        
        if (filteredProperty) {
            setPropertyData(filteredProperty);
        } else {
            setIsLoading(false);
        }
    }, [filteredProperty, setPropertyData]);

    const defaultCenter = { lat: 43.4516, lng: -80.4925 };
    
    // Define the bounds for Ontario
    const ontarioBounds = {
        north: 46.85,     // approximate northern limit
        south: 40.68,     // southern border
        west: -85.16,     // western border
        east: -74.34,     // eastern border
      };

    /**
     * Handles marker click event
     * 
     * @param {Object} location - The location object associated with the clicked marker
     */
    const handleMarkerClick = useCallback((location) => {
        // Remove console.log to prevent unnecessary re-renders
        setActiveMarker(location);
    }, []);

    /**
     * Handles info window close event
     */
    const handleInfoWindowClose = useCallback(() => {
        setActiveMarker(null);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                activeMarker &&
                infoWindowRef.current &&
                !infoWindowRef.current.contains(event.target) &&
                !event.target.closest('.gm-style-iw')
            ) {
                setActiveMarker(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [activeMarker]);

    // Memoize markers to prevent unnecessary re-renders
    const markers = useMemo(() => {
        if (!filteredProperty) return [];
        
        return filteredProperty.map((location, index) => {
            // Safe access to coordinates with fallback
            const position = location?.Unit?.lat && location?.Unit?.long
                ? { lat: Number(location.Unit.lat), lng: Number(location.Unit.long) }
                : defaultCenter;
                
            const propertyTitle = location?.Property?.PropertyUnitTypeTitle || 'Property';
            const propertyType = location?.Unit?.propertSubyType || 'Residential';
            
            return (
                <AdvancedMarker
                    key={index}
                    position={position}
                    title={propertyTitle}
                    clickable={true}
                    onClick={() => handleMarkerClick(location)}
                    role="button"
                    aria-label={`View details for ${propertyTitle}`}
                >
                    {createHouseMarker(propertyType)}
                </AdvancedMarker>
            );
        });
    }, [filteredProperty, handleMarkerClick]);

    // Memoize active marker info window
    const activeMarkerInfoWindow = useMemo(() => {
        if (!activeMarker) return null;
        
        // Ensure required properties exist with safe fallbacks
        const position = activeMarker?.Unit?.lat && activeMarker?.Unit?.long
            ? { lat: Number(activeMarker.Unit.lat), lng: Number(activeMarker.Unit.long) }
            : defaultCenter;
            
        const propertyType = activeMarker?.Unit?.propertSubyType || 'Residential';
        const propertyName = activeMarker?.Property?.Name || 'Property';
        const propertyTitle = activeMarker?.Property?.PropertyUnitTypeTitle || '';
        const propertyId = activeMarker?.Unit?.Id;
        const rent = activeMarker?.Rent || 0;
        const bedrooms = activeMarker?.Unit?.UnitBedrooms;
        const bathrooms = activeMarker?.Unit?.UnitBathrooms;
        const size = activeMarker?.Unit?.UnitSize;
        const propertyImage = activeMarker?.Property?.Files || '';
        
        return (
            <InfoWindow
                position={position}
                onCloseClick={handleInfoWindowClose}
                aria-label={`Property details for ${propertyName}`}
            >
                <div ref={infoWindowRef} className="info-window" role="dialog" aria-modal="true">
                    <div className="info-header flex-md-nowrap flex-wrap">
                        <div className="info-icon">
                            <Image
                                className="property-image"
                                src={propertyImage|| samplePic}
                                alt={propertyName}
                            />
                        </div>
                        <div className="info-details">
                            <div className="property-type">{propertyType}</div>
                            <div className="propertyTitle">
                                {propertyId ? (
                                    <Link 
                                        to={`/listing/${propertyId}`} 
                                        className="property-link"
                                        aria-label={`View details for ${propertyName}`}
                                    >
                                        {propertyName}
                                    </Link>
                                ) : (
                                    <span>{propertyName}</span>
                                )}
                            </div>
                            <div
                                className="rent-amount"
                                style={{
                                    color: getColor(propertyType)
                                }}
                            >
                                <span>${rent.toLocaleString('en-US')}</span>
                            </div>
                            <div className="property-title">
                                <small>{propertyTitle}</small>
                            </div>

                            <div className="property-info">
                                <div className="info-item" aria-label={`${bedrooms || 'N/A'} Bedrooms`}>
                                    <FaBed title='Bedrooms' aria-hidden="true" />
                                    <span>{bedrooms ?? 'N/A'}</span>
                                </div>
                                <div className="info-item" aria-label={`${bathrooms || 'N/A'} Bathrooms`}>
                                    <FaBath title='Bathrooms' aria-hidden="true" />
                                    <span>{bathrooms ?? 'N/A'}</span>
                                </div>
                                <div className="info-item" aria-label={`${size ? `${size} square feet` : 'Size not available'}`}>
                                    <LiaVectorSquareSolid title='Sq Ft' aria-hidden="true" />
                                    <span>
                                        {size && size !== 0
                                            ? `${size} ft²`
                                            : 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <button onClick={handleInfoWindowClose} className="closeBtn">
                        <span aria-label="Close info window">
                            &times;
                        </span>
                    </button>
                </div>
            </InfoWindow>
        );
    }, [activeMarker, handleInfoWindowClose]);

    // Render loading state
    if (isLoading) {
        return (
            <div className="map-loading-container" role="status" aria-live="polite">
                <div className="spinner"></div>
                <p>Loading map data...</p>
            </div>
        );
    }

    // Render error state
    if (hasError) {
        return (
            <div className="map-error-container" role="alert">
                <h3>Error Loading Map</h3>
                <p>{errorMessage || 'Failed to load map. Please try again later.'}</p>
                <button 
                    onClick={() => window.location.reload()}
                    className="reload-button"
                >
                    Reload
                </button>
            </div>
        );
    }

    // Render empty state
    if (locations.length === 0) {
        return (
            <div className="map-empty-container" role="status">
                <p>No property locations available to display on the map.</p>
            </div>
        );
    }

    return (
        <APIProvider apiKey={MAPS_API_KEY}>
            <Map
                defaultCenter={defaultCenter}
                mapTypeId={'hybrid'}
                restriction={{
                    latLngBounds: ontarioBounds,
                    strictBounds: true,
                }}
                defaultZoom={13}
                mapId="property-map"
                style={{ width: '100%', height: '350px' }}
                aria-label="Property locations map"
            >
                {markers}
                {activeMarkerInfoWindow}
            </Map>
        </APIProvider>
    );
};

/**
 * Creates a custom marker icon based on property type
 * 
 * @param {string} propertyType - Type of the property
 * @returns {JSX.Element} - Marker icon component
 */
function createHouseMarker(propertyType) {
    const color = getColor(propertyType || 'Residential');
    return (
        <div className="custom-marker" role="img" aria-label={`${propertyType || 'Property'} location`}>
            <FaMapMarker className="marker-base" color={color} size={50} aria-hidden="true" />
            <div className="marker-icon">
                {getPropertyIcon(propertyType)}
            </div>
        </div>
    );
}

/**
 * Determines the color to use based on property type
 * 
 * @param {string} propertyType - Type of the property
 * @returns {string} - Hex color code
 */
function getColor(propertyType) {
    const type = (propertyType || 'Residential').toLowerCase();
    if (type.includes('commercial')) return '#F7A800FF'; // High contrast commercial color
    if (type.includes('student')) return '#8DAC05FF';    // High contrast student color
    return '#3B67FAFF';                                  // High contrast residential color
}

/**
 * Returns the appropriate icon component based on property type
 * 
 * @param {string} propertyType - Type of the property
 * @param {boolean} infoWin - Flag indicating if the icon is for an info window
 * @returns {JSX.Element} - Icon component
 */
function getPropertyIcon(propertyType, infoWin = false) {
    const color = getColor(propertyType || 'Residential');
    const type = (propertyType || 'Residential').toLowerCase();
    if (type.includes('commercial')) {
        return <FaWarehouse size={infoWin === true ? 50 : 20} color={infoWin === true ? color : "white"} aria-hidden="true" />;
    }
    if (type.includes('student')) {
        return <FaBuilding size={infoWin === true ? 50 : 20} color={infoWin === true ? color : "white"} aria-hidden="true" />;
    }
    return <FaHome size={infoWin === true ? 50 : 20} color={infoWin === true ? color : "white"} aria-hidden="true" />;
}

// PropTypes validation
MultiMap.propTypes = {
    filteredProperty: PropTypes.arrayOf(
        PropTypes.shape({
            Unit: PropTypes.shape({
                lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
                long: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
                propertSubyType: PropTypes.string,
                Id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
                UnitBedrooms: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
                UnitBathrooms: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
                UnitSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
            }),
            Property: PropTypes.shape({
                PropertyUnitTypeTitle: PropTypes.string,
                Name: PropTypes.string,
                Files: PropTypes.any
            }),
            Rent: PropTypes.number
        })
    )
};
